/**
 * Formatting Buttons Component
 * Bold, Italic, Underline buttons for LaTeX toolbar
 */

import React from 'react'
import { EditorView } from '@codemirror/view'
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
} from '@heroicons/react/24/outline'
import {
  BoldIcon as BoldIconSolid,
  ItalicIcon as ItalicIconSolid,
  UnderlineIcon as UnderlineIconSolid,
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { insertBold, insertItalic, insertUnderline, isInsideCommand } from '@/utils/latex/insertionHelpers'

interface FormattingButtonsProps {
  view: EditorView | null
  theme?: 'light' | 'dark'
}

const FormattingButtons: React.FC<FormattingButtonsProps> = ({ view, theme = 'light' }) => {
  const handleBold = () => {
    if (!view) return
    insertBold(view)
    view.focus()
  }

  const handleItalic = () => {
    if (!view) return
    insertItalic(view)
    view.focus()
  }

  const handleUnderline = () => {
    if (!view) return
    insertUnderline(view)
    view.focus()
  }

  // Check if cursor is inside formatting commands (for active state)
  const isBoldActive = view ? isInsideCommand(view, 'textbf') : false
  const isItalicActive = view ? isInsideCommand(view, 'textit') : false
  const isUnderlineActive = view ? isInsideCommand(view, 'underline') : false

  const buttonClass = clsx(
    'p-1.5 rounded transition-colors',
    theme === 'dark'
      ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
      : 'text-gray-800 hover:bg-gray-100 hover:text-gray-900',
    'disabled:opacity-50 disabled:cursor-not-allowed'
  )

  const activeButtonClass = clsx(
    buttonClass,
    theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'
  )

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={handleBold}
        disabled={!view}
        className={isBoldActive ? activeButtonClass : buttonClass}
        title="Bold text (Ctrl+B or Cmd+B) - Inserts \\textbf{}"
        aria-label="Bold text"
      >
        {isBoldActive ? (
          <BoldIconSolid className="w-4 h-4" />
        ) : (
          <BoldIcon className="w-4 h-4" />
        )}
      </button>
      <button
        type="button"
        onClick={handleItalic}
        disabled={!view}
        className={isItalicActive ? activeButtonClass : buttonClass}
        title="Italic text (Ctrl+I or Cmd+I) - Inserts \\textit{}"
        aria-label="Italic text"
      >
        {isItalicActive ? (
          <ItalicIconSolid className="w-4 h-4" />
        ) : (
          <ItalicIcon className="w-4 h-4" />
        )}
      </button>
      <button
        type="button"
        onClick={handleUnderline}
        disabled={!view}
        className={isUnderlineActive ? activeButtonClass : buttonClass}
        title="Underline text (Ctrl+U or Cmd+U) - Inserts \\underline{}"
        aria-label="Underline text"
      >
        {isUnderlineActive ? (
          <UnderlineIconSolid className="w-4 h-4" />
        ) : (
          <UnderlineIcon className="w-4 h-4" />
        )}
      </button>
    </div>
  )
}

export default FormattingButtons

